import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useDragControls } from 'framer-motion';
import { X, Search, HelpCircle, ChevronDown, ChevronRight, BookOpen, Paperclip, Minus, Trash2, Image } from 'lucide-react';
import MediaLibrary from '../../MediaLibrary';
import { uploadbulkFile } from '../../../api/MediaLib';
import { ValidateAppUserToken } from '../../../api/chatbotbuilderapi';
import './HelpPage.css';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import Kit19ChatWidget from '../Kit19ChatWidget/Kit19ChatWidget';

const helpDataCache = new Map();

const HelpPage = ({ isOpen, onClose, pageName = 'Module', pageCode, moduleCode = 'MOD10176', themeColor = '#2563eb' }) => {
    const navigate = useNavigate();
    const dragControls = useDragControls();
    const [isMinimized, setIsMinimized] = useState(!isOpen);
    const [hasBeenOpened, setHasBeenOpened] = useState(isOpen);
    const [isDismissed, setIsDismissed] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const isDraggingRef = useRef(false);
    
    const [showSupportOptions, setShowSupportOptions] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedIds, setExpandedIds] = useState([]);
    const [isTicketPanelOpen, setIsTicketPanelOpen] = useState(false);
    const [isTicketSubmitted, setIsTicketSubmitted] = useState(false);
    const [ticketNumber, setTicketNumber] = useState(null);
    const [ticketSubject, setTicketSubject] = useState('');
    const [ticketDescription, setTicketDescription] = useState('');
    const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [showMediaLibrary, setShowMediaLibrary] = useState(false);
    const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);

    const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
   
    
    const Get_Supporting_Info = `${NEW_BASE_URL}/Common/CommonActionModebased`;
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const storedLogo = localStorage.getItem('Logo');
    
    // We consider them a partner if they have a custom logo that isn't the default Kit19 "logobig.png"
    // (Normal users get either .../img/logobig.png or .../10043/logobig.PNG)
    const isCustomBranding = storedLogo && !storedLogo.toLowerCase().includes('logobig.png');
    
    // It's a Kit19 user if they DO NOT have custom branding.
    // (We do not check TpUserID because regular users can have different TpUserIDs)
    const isKit19User = !isCustomBranding;
    
    const [fetchedData, setFetchedData] = useState([]);

    const enrichHelpData = (data) => {
        if (!Array.isArray(data)) return [];
        return data.map(item => {
            return { ...item };
        });
    };

    useEffect(() => {
        const fetchHelpData = async () => {
            if (!pageCode) {
                setFetchedData([]);
                return;
            }
            const uid = localStorage.getItem('userId') || '34594';
            const CurrentPage = pageCode === "Dashboard" ? (moduleCode || "MOD10176") : (pageCode || moduleCode || "MOD10176");
            const cacheKey = `${uid}_${CurrentPage}`;

            if (helpDataCache.has(cacheKey)) {
                setFetchedData(helpDataCache.get(cacheKey));
                return;
            }

            const payload = {
                Token: "-2295521862261168",
                Details: JSON.stringify({
                    Mode: "GetHelpContent",
                    UserId: parseInt(uid, 10),
                    CurrentPage: CurrentPage,
                    ModuleCode: "",
                    PageCode: pageCode === "Dashboard" ? "Dashboard" : ""
                })
            };
            try {
                const response = await fetch(Get_Supporting_Info, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                const result = await response.json();
                if (result.Status === 1 && result.Details?.[0]?.JsonResult) {
                    const parsed = JSON.parse(result.Details[0].JsonResult);
                    const enriched = enrichHelpData(parsed);
                    helpDataCache.set(cacheKey, enriched);
                    setFetchedData(enriched);
                } else {
                    helpDataCache.set(cacheKey, []);
                    setFetchedData([]);
                }
            } catch (err) {
                console.error('Error fetching help data:', err);
                setFetchedData([]);
            }
        };
        fetchHelpData();
    }, [pageCode, Get_Supporting_Info]);

    useEffect(() => {
        const handleGlobalPaste = async (e) => {
            if (!isTicketPanelOpen) return;
            
            const items = (e.clipboardData || e.originalEvent?.clipboardData)?.items;
            if (!items) return;

            let pastedFile = null;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    pastedFile = items[i].getAsFile();
                    break;
                }
            }

            if (!pastedFile) return;

            // Prevent default to avoid pasting in unwanted places
            e.preventDefault(); 
            
            const ext = pastedFile.type.split('/')[1] || 'png';
            const fileName = `paste_${Date.now()}.${ext}`;
            const newFile = new File([pastedFile], fileName, { type: pastedFile.type });

            const TokenIdStr = sessionStorage.getItem('TokenId') || localStorage.getItem('TokenId');
            const UserIdStr = sessionStorage.getItem('UserId') || localStorage.getItem('UserId') || '34594';
            
            const directory = `${UserIdStr}/Restricted/Lead/`;

            setIsSubmittingTicket(true);
            try {
                const tokenResult = await ValidateAppUserToken(UserIdStr);
                let token = "-2295521862261168"; // Fallback token
                if (tokenResult?.Status === 1) {
                    const parsedData = JSON.parse(tokenResult.Data);
                    const userInfo = parsedData[0];
                    if (userInfo?.token) {
                        token = userInfo.token;
                    }
                }

                await uploadbulkFile(newFile, directory, token);
                const fileUrl = `https://docs.kit19.com/${directory}${fileName}`;

                setAttachments(prev => [
                    ...prev,
                    {
                        name: fileName,
                        url: fileUrl,
                        type: newFile.type,
                        isImage: true
                    }
                ]);
            } catch (error) {
                console.error("Error uploading pasted image", error);
                alert("Error uploading image. Please try again or use the Media Library.");
            } finally {
                setIsSubmittingTicket(false);
            }
        };

        window.addEventListener('paste', handleGlobalPaste);
        return () => window.removeEventListener('paste', handleGlobalPaste);
    }, [isTicketPanelOpen]);

    const handlePasteEvent = async (e) => {
        // Stop propagation to avoid triggering the global listener twice if it bubbles
        e.stopPropagation();

        const items = e.clipboardData?.items || e.originalEvent?.clipboardData?.items;
        if (!items) {
            console.log("No clipboard items found in onPaste");
            return;
        }

        let pastedFile = null;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                pastedFile = items[i].getAsFile();
                break;
            }
        }

        if (!pastedFile) {
            console.log("No image found in textarea paste.");
            return;
        }

        e.preventDefault(); 
        console.log("Image paste detected in textarea!");
        
        const ext = pastedFile.type.split('/')[1] || 'png';
        const fileName = `paste_${Date.now()}.${ext}`;
        const newFile = new File([pastedFile], fileName, { type: pastedFile.type });

        const TokenIdStr = sessionStorage.getItem('TokenId') || localStorage.getItem('TokenId');
        const UserIdStr = sessionStorage.getItem('UserId') || localStorage.getItem('UserId') || '34594';
        
        const directory = `${UserIdStr}/Restricted/Lead/`;

        setIsSubmittingTicket(true);
        try {
            const tokenResult = await ValidateAppUserToken(UserIdStr);
            let token = "-2295521862261168"; // Fallback token
            if (tokenResult?.Status === 1) {
                const parsedData = JSON.parse(tokenResult.Data);
                const userInfo = parsedData[0];
                if (userInfo?.token) {
                    token = userInfo.token;
                }
            }

            await uploadbulkFile(newFile, directory, token);
            const fileUrl = `https://docs.kit19.com/${directory}${fileName}`;

            setAttachments(prev => [
                ...prev,
                {
                    name: fileName,
                    url: fileUrl,
                    type: newFile.type,
                    isImage: true
                }
            ]);
        } catch (error) {
            console.error("Error uploading pasted image", error);
            alert("Error uploading image. Please try again or use the Media Library.");
        } finally {
            setIsSubmittingTicket(false);
        }
    };

    const handleMediaSelect = (media) => {
        setAttachments(prev => [...prev, { name: media.name || media.title || 'Attached File', url: media.url, isImage: media.type === 'image' || media.url?.match(/\.(jpeg|jpg|gif|png)$/) != null }]);
    };

    const handleTicketSubmit = async () => {
        if (!ticketSubject.trim()) {
            alert('Subject is required');
            return;
        }

        setIsSubmittingTicket(true);
        try {
            const TokenIdStr = sessionStorage.getItem('TokenId') || localStorage.getItem('TokenId');
            const UserIdStr = sessionStorage.getItem('UserId') || localStorage.getItem('UserId');
            const TpUserIdStr = sessionStorage.getItem('TpUserID') || localStorage.getItem('TpUserID');
            
            const TokenIdNum = TokenIdStr ? Number(TokenIdStr) : -2295521862261168;
            const UserIdNum = UserIdStr ? Number(UserIdStr) : 34594;
            const TpUserIdNum = TpUserIdStr ? Number(TpUserIdStr) : 1;
            
            const payload = {
                Token: TokenIdNum,
                LoggedUserId: UserIdNum,
                Message: "",
                MAC_Address: "",
                IP_Address: "",
                Details: {
                    TpUserId: TpUserIdNum,
                    Company: "",
                    ParentId: UserIdNum,
                    TagIDs: "",
                    Ticket_Status: 1,
                    TicketId: 0,
                    Option: "Insert",
                    Ticket_Priority: 3,
                    Ticket_Type: 1,
                    Ticket_Category: 1,
                    Ticket_Product: "5465",
                    Ticket_Tags: "",
                    Ticket_Subject: ticketSubject,
                    Ticket_Description: ticketDescription,
                    CreatedBy: TpUserIdNum,
                    Attachement: attachments.length > 0 ? JSON.stringify(attachments.map(a => ({ FileName: a.name, FilePath: a.url }))) : "[]",
                    CustomFieldValueJson: "{}",
                    AssignedSource: "Customer",
                    MetaData: "{\"IPAddress\":\"182.75.38.29\",\"MacAddress\":\"\",\"IsImpersonation\":false,\"ImpersonatorId\":0}"
                }
            };

            const response = await fetch(`${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.ADD_TECKET_NEW}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.Status === 1) {
                const successMsg = result.Details?.data?.[0]?.Column1 || result.Message || "Ticket Raised Successfully";
                setTicketNumber(successMsg);
                setIsTicketSubmitted(true);
            } else {
                console.error("Failed to submit ticket:", result);
                alert("Failed to submit ticket: " + (result.Message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error submitting ticket:", error);
            alert("Error submitting ticket. Please try again.");
        } finally {
            setIsSubmittingTicket(false);
        }
    };

    const resetTicketPanel = () => {
        setIsTicketPanelOpen(false);
        setTimeout(() => {
            setIsTicketSubmitted(false);
            setTicketNumber(null);
            setTicketSubject('');
            setTicketDescription('');
            setAttachments([]);
        }, 300);
    };
    
    useEffect(() => {
        if (isOpen) {
            setHasBeenOpened(true);
            setIsDismissed(false);
            setIsMinimized(false);
            setIsTicketPanelOpen(false);
            setIsChatPanelOpen(false);
            setIsTicketSubmitted(false);
            setShowSupportOptions(false);
            setSearchQuery('');
            setExpandedIds([]);
        } else {
            setIsMinimized(true);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isMinimized && expandedIds.length === 0) {
            setSearchQuery('');
            setExpandedIds([0]);
        }
    }, [isMinimized, pageName]);

    const handleMinimize = () => {
        setIsMinimized(true);
        onClose();
    };

    const handleMaximize = () => {
        if (isDraggingRef.current) return;
        setIsMinimized(false);
    };

    const handleDragStart = () => {
        isDraggingRef.current = true;
        setIsDragging(true);
    };

    const handleDragEnd = (event, info) => {
        setIsDragging(false);
        
        const dragDistance = Math.sqrt(info.offset.x * info.offset.x + info.offset.y * info.offset.y);
        if (dragDistance > 5) {
            isDraggingRef.current = true;
            setTimeout(() => { isDraggingRef.current = false; }, 100);
        } else {
            isDraggingRef.current = false;
        }

        const sh = window.innerHeight;
        const sw = window.innerWidth;
        if (info.point.y > sh - 120 && Math.abs(info.point.x - sw / 2) < 150) {
            setIsDismissed(true);
            onClose();
        }
    };

    if (isDismissed) return null;
    if (!hasBeenOpened) return null;

    const questions = fetchedData || [];

    const filteredQuestions = questions.filter(q => 
        q.q.toLowerCase().includes(searchQuery.toLowerCase()) || 
        q.a.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const toggleExpand = (index) => {
        if (expandedIds.includes(index)) {
            setExpandedIds(expandedIds.filter(id => id !== index));
        } else {
            setExpandedIds([...expandedIds, index]);
        }
    };

    return (
        <>
            {isDragging && (
                <div className="help-drop-zone">
                    <Trash2 size={24} />
                    <span>Drop to Remove</span>
                </div>
            )}
            
            {isMinimized ? (
                <motion.div
                    key="help-fab"
                    drag
                    dragMomentum={false}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    className="help-fab"
                    onClick={handleMaximize}
                    style={{ '--help-theme-color': themeColor }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Need Help?"
                >
                    <HelpCircle size={28} />
                </motion.div>
            ) : (
                <motion.div 
                    key="help-drawer"
                    drag
                    dragControls={dragControls}
                    dragListener={false}
                    dragMomentum={false}
                    className="help-drawer"
                    style={{ '--help-theme-color': themeColor }}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                >
                    <div 
                        className="help-drawer-header drag-handle"
                        onPointerDown={(e) => dragControls.start(e)}
                        style={{ cursor: 'grab', touchAction: 'none' }}
                    >
                        <div className="header-title">
                            <BookOpen size={20} className="header-icon" />
                            <h2>Help: {pageName || 'Module'}</h2>
                        </div>
                        <div className="header-actions">
                            <button className="close-btn" onClick={handleMinimize} title="Minimize to icon">
                                <Minus size={20} />
                            </button>
                            <button className="close-btn" onClick={handleMinimize} title="Close Help">
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                
                <div className="help-drawer-search">
                        <div className="search-input-wrapper">
                            <Search size={16} className="search-icon" />
                            <input 
                                type="text" 
                                className="help-search-input"
                                placeholder="Search help topics..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                            <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="help-drawer-content">
                    {filteredQuestions.length === 0 ? (
                        <div className="no-results">
                            <HelpCircle size={40} className="no-results-icon" />
                            <p>No matching help topics found.</p>
                            {searchQuery && <button onClick={() => setSearchQuery('')}>Clear Search</button>}
                        </div>
                    ) : (
                        <div className="faq-list">
                            {filteredQuestions.map((item, index) => {
                                const isExpanded = expandedIds.includes(index);
                                return (
                                    <div key={index} className={`faq-item ${isExpanded ? 'expanded' : ''}`}>
                                        <div className="faq-question" onClick={() => toggleExpand(index)}>
                                            <h3>{item.q}</h3>
                                            {isExpanded ? <ChevronDown size={18} className="chevron" /> : <ChevronRight size={18} className="chevron" />}
                                        </div>
                                        <div className="faq-answer">
                                            <p>{item.a}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                
                <div className="help-drawer-footer">
                    <p>Can't find what you're looking for?</p>
                    <button className="contact-support-btn" onClick={() => setIsTicketPanelOpen(true)}>
                        Contact Support
                    </button>
                    {isKit19User && (
                        <button className="contact-support-btn" style={{ marginTop: '8px', background: 'linear-gradient(135deg, #25a244, #1a7d34)', color: '#fff' }} onClick={() => setIsChatPanelOpen(true)}>
                            Chat with Kit19 AI
                        </button>
                    )}
                </div>

                {/* Inner Ticket Modal Panel */}
                <div className={`ticket-panel-modal ${isTicketPanelOpen ? 'open' : ''}`}>
                    <div 
                        className="ticket-panel-header drag-handle"
                        onPointerDown={(e) => dragControls.start(e)}
                        style={{ cursor: 'grab', touchAction: 'none' }}
                    >
                        <h3>Manage - Ticket</h3>
                        <button className="close-btn" onPointerDown={(e) => e.stopPropagation()} onClick={resetTicketPanel}>
                            <X size={20} />
                        </button>
                    </div>
                    
                    {isTicketSubmitted ? (
                        <div className="ticket-success-message">
                            <div className="success-icon-wrapper">
                                <span style={{ fontSize: '40px' }}>✅</span>
                            </div>
                            <h3>{ticketNumber}</h3>
                        </div>
                    ) : (
                        <>
                            <div className="ticket-panel-body">
                                <div className="form-group">
                                    <label className="required">Subject</label>
                                    <input 
                                        type="text" 
                                        className="subject-input" 
                                        placeholder="What do you need help with?" 
                                        value={ticketSubject}
                                        onChange={(e) => setTicketSubject(e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Description</label>
                                    <div className="rich-text-editor">
                                        <textarea 
                                            className="rte-textarea" 
                                            placeholder="Describe your issue in detail... (You can paste screenshots here with Ctrl+V)" 
                                            style={{ minHeight: '200px' }}
                                            value={ticketDescription}
                                            onChange={(e) => setTicketDescription(e.target.value)}
                                            onPaste={handlePasteEvent}
                                        ></textarea>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Attachment</label>
                                    <button className="attach-file-btn" onClick={() => setShowMediaLibrary(true)}>
                                        <Paperclip size={16} /> Select from Media Library
                                    </button>
                                    {attachments.length > 0 && (
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                                            {attachments.map((att, i) => (
                                                <div key={i} title={att.name} style={{ position: 'relative', width: '60px', height: '60px', border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f5f9' }}>
                                                    {att.isImage ? (
                                                        <img src={att.url} alt="attachment" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <div style={{ fontSize: '10px', textAlign: 'center', wordBreak: 'break-all', padding: '4px', color: '#475569' }}>
                                                            {att.name.length > 15 ? att.name.substring(0,12)+'...' : att.name}
                                                        </div>
                                                    )}
                                                    <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', padding: '2px', zIndex: 10 }}>
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="ticket-panel-footer">
                                <button className="btn-cancel" onClick={resetTicketPanel} disabled={isSubmittingTicket}>Cancel</button>
                                <button className="btn-submit" onClick={handleTicketSubmit} disabled={isSubmittingTicket}>
                                    {isSubmittingTicket ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* Inner Chat Modal Panel */}
                <div className={`ticket-panel-modal ${isChatPanelOpen ? 'open' : ''}`} style={{ padding: 0, overflow: 'hidden' }}>
                    <div 
                        className="ticket-panel-header drag-handle"
                        onPointerDown={(e) => dragControls.start(e)}
                        style={{ cursor: 'grab', touchAction: 'none' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <img src="/assets/custom/NewIndexPage/logobig.png" alt="Kit19" style={{ height: '24px', objectFit: 'contain' }} />
                            <h3 style={{ margin: 0 }}>Kit19 AI Assistant</h3>
                        </div>
                        <button className="close-btn" onPointerDown={(e) => e.stopPropagation()} onClick={() => setIsChatPanelOpen(false)}>
                            <X size={20} />
                        </button>
                    </div>
                    <div style={{ height: 'calc(100% - 60px)', position: 'relative' }}>
                        {isChatPanelOpen && <Kit19ChatWidget embedded={true} />}
                    </div>
                </div>

                </motion.div>
            )}

            {showMediaLibrary && (
                <MediaLibrary
                    show={showMediaLibrary}
                    onclose={() => setShowMediaLibrary(false)}
                    onSelectMedia={handleMediaSelect}
                />
            )}
        </>
    );
};

export default HelpPage;
